import { Divider, Required, WizardFooter } from "@components"
import { maxBookingsOptions, maxBookingsRestrictedDaysOptions } from "@config"
import { Select, SelectItem, Switch } from "@heroui/react"
import { Controller, useForm } from "react-hook-form";
import { PropTypes } from "prop-types"

const EventSettings = ({
  includeSecondaryContacts = false,
  vcAutoDisconnect = false, 
  maxBookingsPerStudent = 0,
  maxBookingsRestrictedDays = 0,
  showWizardFooter = true, 
  venueId = null, 
  isVcEvent, 
  isVenueEvent, 
  handlePrevious = () => {}, 
  handleSubmit, 
  venues = []
}) => {
  const { control, handleSubmit: handleValidateForm, formState: { errors }, getValues, setValue, watch } = useForm({
    defaultValues: {
      include_secondary_contacts: includeSecondaryContacts,
      vc_auto_disconnect: vcAutoDisconnect,
      max_bookings_per_student: maxBookingsPerStudent,
      max_bookings_restricted_days: maxBookingsRestrictedDays,
      venue_id: venueId,
    }
  })

  const handleMaxBookingsChange = (event, onChange) => {
    const value = Number(event.target.value)

    onChange(value)

    if (value === 0) {
      setValue("max_bookings_restricted_days", 0)
    }
  }

  const maxBookings = Number(watch("max_bookings_per_student"))

  const maxBookingsDropdown = (
    <Controller
      control={control}
      name="max_bookings_per_student"
      rules={{ required: "You must supply a maximum bookings per student" }}
      render={({ field: { value, onChange } }) => (
        <Select 
          classNames={{ base: "shrink-0 w-[250px]" }}
          isRequired
          disallowEmptySelection={true}
          onChange={(event) => handleMaxBookingsChange(event, onChange)}
          selectionMode="single"
          defaultSelectedKeys={[value.toString()]}
          isInvalid={errors.hasOwnProperty("max_bookings_per_student")}
          aria-label="Max bookings per student"
        >
          {maxBookingsOptions.map((option) => (
            <SelectItem key={option.id.toString()} value={option.id}>{option.label}</SelectItem>
          ))}
        </Select>
      )}
    />
  )

  const maxBookingsRestrictedDaysDropdown = (
    <Controller
      control={control}
      name="max_bookings_restricted_days"
      rules={{ required: "You must supply a value for max bookings days" }}
      render={({ field: { value, onChange } }) => (
        <Select
          key={`max-bookings-restricted-days-${maxBookings}-${value}`}
          classNames={{ base: "shrink-0 w-[280px]" }}
          isRequired
          disallowEmptySelection={true}
          onChange={(event) => onChange(Number(event.target.value))}
          selectionMode="single"
          defaultSelectedKeys={[value.toString()]}
          isInvalid={errors.hasOwnProperty("max_bookings_restricted_days")}
          aria-label="Max bookings days"
          isDisabled={maxBookings === 0}
        >
        {maxBookingsRestrictedDaysOptions.map((option) => (
          <SelectItem key={option.id.toString()} value={option.id}>{option.label}</SelectItem>
        ))}
      </Select>
      )}
    />
  )

  return (
    <form id="event-settings-form" className="flex flex-col gap-y-4" noValidate>
      <div>
        <h5 className="mb-1 text-base font-semibold text-foreground">Who to invite</h5>
        <p className="mb-3 text-sm text-content2-foreground">Select primary contacts only or include secondary contacts (This is MIS dependant).</p>

        <Controller
          control={control}
          name="include_secondary_contacts"
          render={({ field: { value, onChange } }) => (
            <Switch defaultSelected={value} onChange={onChange}>Include Secondary Contacts</Switch>
          )}
        />

        <Divider className="mt-4" />
      </div>

      {isVcEvent && (
        <div>
          <h5 className="mb-1 text-base font-semibold text-foreground">Video Call Options</h5>
          <p className="mb-3 text-sm text-content2-foreground">Automatically disconnect calls once slot time has expired.</p>

          <Controller
            control={control}
            name="vc_auto_disconnect"
            render={({ field: { value, onChange } }) => (
              <Switch defaultSelected={value} onChange={onChange}>Auto Disconnect</Switch>
            )}
          />

          <Divider className="mt-4" />
        </div>
      )}
      
      {isVenueEvent && (
        <div>
          <h5 className="mb-1 text-base font-semibold text-foreground">Venue Options<Required /></h5>
          <p id="venue-label" className="mb-0 text-sm text-content2-foreground">Select where the event will take place.</p>

          <Controller
            control={control}
            rules={{ required: "You must supply a venue for your event" }}
            name="venue_id"
            render={({ field: { value, onChange } }) => (
              <Select
                classNames={{ "label": "mt-2", "mainWrapper": "mt-3" }}
                isRequired
                placeholder="Select a venue"
                disallowEmptySelection={true}
                onChange={onChange}
                selectionMode="single"
                defaultSelectedKeys={[value]}
                isInvalid={errors.hasOwnProperty("venue_id")}
                aria-labelledby="venue-label"
              >
                {venues.map((venue) => (
                  <SelectItem key={venue.id} value={venue.id}>
                    {venue.name}
                  </SelectItem>
                ))}
              </Select>
            )}
          />

          <Divider className="mt-4" />
        </div>
      )}

      <div>
        <h5 className="mb-1 text-base font-semibold text-foreground">Booking Limits</h5>
        <p className="mb-3 text-sm text-content2-foreground">
          Set the maximum bookings a guardian can make and how long the restriction applies.
        </p>

        <div className="mt-3">          
          <div className="flex flex-wrap items-center justify-between">
            <span className="flex items-center mb-1">
              <span className="mr-2 text-sm">Limit<Required /></span> {maxBookingsDropdown}
            </span>

            <span className="flex items-center mb-1">
              <span className="mr-2 text-sm">Duration<Required /></span> {maxBookingsRestrictedDaysDropdown}
            </span>
          </div>
        </div>
      </div>
      
      {showWizardFooter && (
        <WizardFooter currentStep={1} totalSteps={3} previousCaption="Meeting Types" nextCaption="Sessions" handlePrevious={() => handlePrevious(getValues())} handleNext={handleValidateForm((data) => handleSubmit(data))} />
      )}
    </form>
  )
}

EventSettings.propTypes = {
  includeSecondaryContacts: PropTypes.bool,
  vcAutoDisconnect: PropTypes.bool,
  isVcEvent: PropTypes.bool.isRequired,
  isVenueEvent: PropTypes.bool.isRequired,
  handlePrevious: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  maxBookingsPerStudent: PropTypes.number,
  maxBookingsRestrictedDays: PropTypes.number,
  showWizardFooter: PropTypes.bool,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  venues: PropTypes.array
}

export default EventSettings